import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';

// ============================================================
// FUNCIÓN PARA AGRUPAR VENTAS POR CANAL
// ============================================================
const agruparVentasPorCanal = (movimientos) => {
  const ventas = movimientos?.filter(m => 
    m.tipo === 'ingreso' && 
    m.tipoOperacion !== 'devolucion_venta' &&
    m.tipoOperacion === 'venta'
  ) || [];

  const porCanal = {};
  ventas.forEach(v => {
    const canal = v.canalVenta || v.plataforma || 'Otros';
    if (!porCanal[canal]) {
      porCanal[canal] = { ventas: 0, utilidad: 0, margen: 0 };
    }
    porCanal[canal].ventas += (v.valor || 0);
    const utilidad = v.utilidadNetaAuditada || (v.valor * 0.3);
    porCanal[canal].utilidad += utilidad;
  });

  Object.keys(porCanal).forEach(canal => {
    const data = porCanal[canal];
    data.margen = data.ventas > 0 ? ((data.utilidad / data.ventas) * 100) : 0;
  });

  return porCanal;
};

const DashboardInsights = ({ 
  saldoCaja, 
  ventasTotales, 
  gastosTotales, 
  margenNeto,
  diasInactividad,
  productosEstrella,
  productosHueso,
  gastosFijosMensuales,
  tendenciaVentas,
  usuarioActual,
  idioma,
  movimientos = []
}) => {
  const { t } = useTranslation();
  
  const [diasCubiertosRealtime, setDiasCubiertosRealtime] = useState(0);
  const [ultimoMovimientoFecha, setUltimoMovimientoFecha] = useState(null);

  const ventasPorCanal = agruparVentasPorCanal(movimientos);
  const canales = Object.keys(ventasPorCanal);
  
  let mejorCanal = '';
  let mejorCanalMargen = -Infinity;
  let mejorCanalVentas = 0;
  let peorCanal = '';
  let peorCanalMargen = Infinity;
  let peorCanalVentas = 0;
  
  canales.forEach(canal => {
    const data = ventasPorCanal[canal];
    if (data.margen > mejorCanalMargen && data.ventas > 0) {
      mejorCanalMargen = data.margen;
      mejorCanalVentas = data.ventas;
      mejorCanal = canal;
    }
    if (data.margen < peorCanalMargen && data.ventas > 0) {
      peorCanalMargen = data.margen;
      peorCanalVentas = data.ventas;
      peorCanal = canal;
    }
  });

  console.log('🔍 DashboardInsights - gastosFijosMensuales:', gastosFijosMensuales);
  console.log('🔍 DashboardInsights - saldoCaja:', saldoCaja);
  console.log('🔍 DashboardInsights - canales:', canales);

  const gastoDiario = gastosFijosMensuales > 0 ? gastosFijosMensuales / 30 : 0;
  const diasCubiertosBase = gastoDiario > 0 ? parseFloat((saldoCaja / gastoDiario).toFixed(1)) : 0;

  const calcularDiasDesdeUltimoMovimiento = () => {
    return diasInactividad || 0;
  };

  useEffect(() => {
    const diasTranscurridos = calcularDiasDesdeUltimoMovimiento();
    const diasAjustados = Math.max(0, diasCubiertosBase - diasTranscurridos);
    setDiasCubiertosRealtime(diasAjustados);
    
    console.log('🔍 Días cubiertos - Base:', diasCubiertosBase);
    console.log('🔍 Días de inactividad:', diasTranscurridos);
    console.log('🔍 Días cubiertos ajustados:', diasAjustados);
  }, [diasCubiertosBase, diasInactividad]);

  const diasCubiertos = diasCubiertosRealtime;

  console.log('🔍 DashboardInsights - gastoDiario:', gastoDiario);
  console.log('🔍 DashboardInsights - diasCubiertos (final):', diasCubiertos);
  
  const getUmbralDiasAlerta = () => {
    const plan = usuarioActual?.plan || 'starter';
    const umbrales = {
      starter: 15,
      pro: 30,
      business: 60,
      elite: 90
    };
    return umbrales[plan] || 15;
  };

  const DIAS_UMBRAL = getUmbralDiasAlerta();
  const necesitaAccion = diasCubiertos < DIAS_UMBRAL;

  const nombreUsuario = usuarioActual?.displayName?.split(' ')[0] || 
                        usuarioActual?.email?.split('@')[0] || 
                        'Emprendedor';

  const getMensajeBienvenida = () => {
    const hora = new Date().getHours();
    const saludo = hora < 12 ? t('goodMorning') : hora < 19 ? t('goodAfternoon') : t('goodEvening');
    
    if (diasCubiertos < 2) {
      return idioma === 'en'
        ? `${saludo}, ${nombreUsuario}. 🚨 CRITICAL CAUTION! Your cash covers less than 2 days of operation.`
        : `${saludo}, ${nombreUsuario}. 🚨 ¡ALERTA CRÍTICA! Tu caja no cubre ni 2 días de operación.`;
    }
    if (diasInactividad > 3) {
      return idioma === 'en'
        ? `${saludo}, ${nombreUsuario}. 📢 You haven't recorded transactions for ${diasInactividad} days.`
        : `${saludo}, ${nombreUsuario}. 📢 Llevas ${diasInactividad} días sin registrar movimientos.`;
    }
    if (margenNeto < 15 && margenNeto > 0) {
      return idioma === 'en'
        ? `${saludo}, ${nombreUsuario}. 📉 Your net margin is ${margenNeto}%. Review your costs.`
        : `${saludo}, ${nombreUsuario}. 📉 Tu margen neto es ${margenNeto}%. Revisa tus costos.`;
    }
    if (productosHueso && productosHueso.length > 0) {
      return idioma === 'en'
        ? `${saludo}, ${nombreUsuario}. 🦴 You have ${productosHueso.length} products without rotation.`
        : `${saludo}, ${nombreUsuario}. 🦴 Tienes ${productosHueso.length} productos sin rotación.`;
    }
    if (productosEstrella && productosEstrella.length > 0) {
      return idioma === 'en'
        ? `${saludo}, ${nombreUsuario}. ⭐ Your star products generated $${productosEstrella[0]?.total?.toLocaleString()}.`
        : `${saludo}, ${nombreUsuario}. ⭐ Tus productos estrella generaron $${productosEstrella[0]?.total?.toLocaleString()}.`;
    }
    if (ventasTotales > 0 && ventasTotales > gastosTotales) {
      return idioma === 'en'
        ? `${saludo}, ${nombreUsuario}. ✅ Your business is generating profit. Keep it up.`
        : `${saludo}, ${nombreUsuario}. ✅ Tu negocio está generando utilidad. Sigue así.`;
    }
    if (ventasTotales > 0) {
      return idioma === 'en'
        ? `${saludo}, ${nombreUsuario}. 📊 You have recorded $${ventasTotales.toLocaleString()} in sales. Good job!`
        : `${saludo}, ${nombreUsuario}. 📊 Ya registraste ventas por $${ventasTotales.toLocaleString()}. ¡Bien!`;
    }
    return `${saludo}, ${nombreUsuario}. ${t('letsTakeCare')}`;
  };

  const getMensajePrincipal = () => {
    if (diasCubiertos < 2) {
      return {
        texto: idioma === 'en'
          ? `🚨 RISK OF LIQUIDITY CRISIS: Cash pool is almost empty (${diasCubiertos} days left). Immediate cash injection or urgent sales needed.`
          : `🚨 RIESGO DE ILIQUIDEZ: Tu caja está al límite (${diasCubiertos} días disponibles). Necesitas inyectar capital o acelerar ventas ya.`,
        color: 'text-red-400',
        bg: 'bg-red-900/30',
        icono: '🚨'
      };
    }

    if (diasCubiertos < DIAS_UMBRAL) {
      const reservaIdeal = DIAS_UMBRAL * gastoDiario;
      const montoFaltante = Math.max(0, reservaIdeal - saldoCaja).toFixed(0);
      
      return {
        texto: idioma === 'en'
          ? `⚠️ BACKGROUND EMERGENCY ALERT: Your cash covers ${diasCubiertos} days. To complete the ${DIAS_UMBRAL}-day security reserve for your Plan, you need to accumulate $${parseInt(montoFaltante).toLocaleString()} in cash.`
          : `⚠️ FONDO DE SEGURIDAD INCOMPLETO: Tu caja cubre ${diasCubiertos} días. Para completar la reserva de seguridad de ${DIAS_UMBRAL} días de tu Plan, necesitas acumular $${parseInt(montoFaltante).toLocaleString()} adicionales en caja.`,
        color: 'text-yellow-400',
        bg: 'bg-yellow-900/30',
        icono: '⚠️'
      };
    }

    return {
      texto: idioma === 'en'
        ? `✅ FINANCIAL COMFORT: Your current cash covers more than ${DIAS_UMBRAL} days of fixed expenses. Outstanding financial management.`
        : `✅ COLCHÓN FINANCIERO SEGURO: Tu caja actual cubre más de ${DIAS_UMBRAL} días de costos fijos. Excelente gestión de tu disponible.`,
      color: 'text-green-400',
      bg: 'bg-green-900/30',
      icono: '✅'
    };
  };

  const mensajeBienvenida = getMensajeBienvenida();
  const mensaje = getMensajePrincipal();

  return (
    <div className="space-y-4 mb-6">
      {/* Saludo personalizado dinámico */}
      <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 p-4 rounded-xl border border-cyan-500/20">
        <p className="text-cyan-400 text-lg font-bold">
          {mensajeBienvenida}
        </p>
      </div>

      {/* Alerta de Auditoría Central */}
      <div className={`${mensaje.bg} p-4 rounded-xl border-l-4 ${mensaje.color === 'text-red-400' ? 'border-red-500' : mensaje.color === 'text-yellow-400' ? 'border-yellow-500' : 'border-green-500'}`}>
        <div className="flex items-start gap-3">
          <span className="text-2xl">{mensaje.icono}</span>
          <p className={`text-sm ${mensaje.color}`}>{mensaje.texto}</p>
        </div>
      </div>

      {/* Insights tácticos - Grid horizontal */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Oxígeno financiero */}
        <div className="bg-[#1e293b] rounded-xl p-4 border border-blue-900/30">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-indigo-300 text-sm font-bold flex items-center gap-2">
              😴 {t('sleepIndicator') || 'Días de Supervivencia'}
            </h4>
            <span className={`text-sm font-bold ${necesitaAccion ? 'text-red-400' : 'text-green-400'}`}>
              {Math.floor(diasCubiertos)} / {DIAS_UMBRAL} {t('days') || 'días'}
            </span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2 mb-3">
            <div 
              className={`h-2 rounded-full transition-all ${necesitaAccion ? 'bg-amber-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(100, (diasCubiertos / DIAS_UMBRAL) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-400">
            {t('covered') || 'Disponible hoy'}: ${saldoCaja.toLocaleString()} | 
            Meta Plan: ${(DIAS_UMBRAL * gastoDiario).toLocaleString()}
          </p>
          {diasInactividad > 0 && (
            <p className="text-xs text-orange-400 mt-2">
              ⚠️ {diasInactividad} {idioma === 'en' ? 'days without sales' : 'días sin ventas'}
            </p>
          )}
        </div>

        {/* ============================================================
            ALERTAS DEL SARGENTO - 100% BILINGÜE
            ============================================================ */}
        <div className="bg-[#1e293b] rounded-xl p-4 border border-blue-900/30">
          <h4 className="text-red-400 text-sm font-bold mb-2 flex items-center gap-2">
            🚨 {idioma === 'en' ? 'Financial Sergeant Alerts' : 'Alertas del Sargento Financiero'}
          </h4>
          <div className="space-y-2">
            {diasCubiertos < DIAS_UMBRAL && (
              <p className="text-xs text-yellow-400">
                ⚠️ {idioma === 'en' 
                  ? `Cash coverage below your plan's target (${DIAS_UMBRAL} days)`
                  : `Cobertura de caja por debajo del objetivo de tu plan (${DIAS_UMBRAL} días)`}
              </p>
            )}
            {diasInactividad > 3 && (
              <p className="text-xs text-blue-400">
                📢 {idioma === 'en'
                  ? `${diasInactividad} days without recording operations in the system`
                  : `${diasInactividad} días sin registrar operaciones en el sistema`}
              </p>
            )}
            {margenNeto < 15 && margenNeto > 0 && (
              <p className="text-xs text-orange-400">
                📉 {idioma === 'en'
                  ? `Net margin (${margenNeto}%) below the recommended minimum (15%)`
                  : `Margen neto (${margenNeto}%) por debajo del mínimo recomendado (15%)`}
              </p>
            )}
            {productosHueso && productosHueso.length > 0 && (
              <p className="text-xs text-red-400">
                🦴 {idioma === 'en'
                  ? `${productosHueso.length} Bone Products (no rotation)`
                  : `${productosHueso.length} Productos Hueso (sin rotación)`}
              </p>
            )}
            {peorCanal && peorCanalMargen < 30 && (
              <p className="text-xs text-orange-400">
                📊 {idioma === 'en'
                  ? `Channel "${peorCanal}" has low margin (${peorCanalMargen.toFixed(1)}%). Review pricing or commissions.`
                  : `El canal "${peorCanal}" tiene bajo margen (${peorCanalMargen.toFixed(1)}%). Revisa precios o comisiones.`}
              </p>
            )}
            {canales.length === 1 && canales[0] && (
              <p className="text-xs text-blue-400">
                📌 {idioma === 'en'
                  ? `100% of sales are concentrated in "${canales[0]}". Diversify your sales channels.`
                  : `El 100% de las ventas están concentradas en "${canales[0]}". Diversifica tus canales de venta.`}
              </p>
            )}
            {!necesitaAccion && diasInactividad <= 3 && margenNeto >= 15 && (!productosHueso || productosHueso.length === 0) && (
              <p className="text-xs text-green-400">
                {idioma === 'en'
                  ? '✅ Operational indicators under control.'
                  : '✅ Indicadores operativos bajo control.'}
              </p>
            )}
          </div>
        </div>

        {/* ============================================================
            RECOMENDACIONES ESTRATÉGICAS - 100% BILINGÜE
            ============================================================ */}
        <div className="bg-[#1e293b] rounded-xl p-4 border border-blue-900/30">
          <h4 className="text-blue-400 text-sm font-bold mb-2 flex items-center gap-2">
            💡 {idioma === 'en' ? 'Strategic Recommendations' : 'Recomendaciones Estratégicas'}
          </h4>
          <div className="space-y-2">
            
            {/* ✅ 1. Crisis de liquidez */}
            {diasCubiertos < 7 && (
              <p className="text-xs text-red-400">
                🔥 {idioma === 'en'
                  ? 'Priority 1: Manage collection of overdue invoices or launch a quick liquidity offer.'
                  : 'Prioridad 1: Gestionar cobro de facturas vencidas o lanzar oferta de liquidez líquida.'}
              </p>
            )}
            
            {/* ✅ 2. Sin ventas registradas */}
            {ventasTotales === 0 && (
              <p className="text-xs text-yellow-400">
                📢 {idioma === 'en'
                  ? "You haven't recorded sales yet. Activate your commercial strategy to start generating revenue."
                  : 'Aún no has registrado ventas. Activa tu estrategia comercial para empezar a generar ingresos.'}
              </p>
            )}
            
            {/* ✅ 3. Producto estrella */}
            {productosEstrella && productosEstrella.length > 0 && (
              <p className="text-xs text-green-400">
                ⭐ {idioma === 'en'
                  ? `Boost the product: "${productosEstrella[0]?.nombre}". It's your biggest cash flow generator.`
                  : `Potencia el producto: "${productosEstrella[0]?.nombre}". Es tu mayor generador de flujo.`}
              </p>
            )}
            
            {/* ✅ 4. Productos hueso */}
            {productosHueso && productosHueso.length > 0 && (
              <p className="text-xs text-orange-400">
                🦴 {idioma === 'en'
                  ? `Bone: You have ${productosHueso.length} products without rotation. Liquidate them at cost to recover cash.`
                  : `Bone: Tienes ${productosHueso.length} referencias sin rotación. Líquidalas al costo para recuperar caja.`}
              </p>
            )}
            
            {/* ✅ 5. Margen bajo */}
            {margenNeto < 20 && margenNeto > 0 && (
              <p className="text-xs text-red-400">
                💰 {idioma === 'en'
                  ? 'Your margins are low. Review costs or selling prices.'
                  : 'Tus márgenes están bajos. Revisa costos o precios de venta.'}
              </p>
            )}

            {/* ✅ 6. Mejor canal */}
            {mejorCanal && mejorCanalMargen >= 30 && (
              <p className="text-xs text-green-400">
                🎯 {idioma === 'en'
                  ? `Focus sales on "${mejorCanal}" (${mejorCanalMargen.toFixed(1)}% margin). It's your most profitable channel.`
                  : `Enfoca las ventas en "${mejorCanal}" (${mejorCanalMargen.toFixed(1)}% margen). Es tu canal más rentable.`}
              </p>
            )}

            {/* ✅ 7. Canal con bajo margen */}
            {peorCanal && peorCanalMargen < 30 && (
              <p className="text-xs text-yellow-400">
                📊 {idioma === 'en'
                  ? `Channel "${peorCanal}" has low margin (${peorCanalMargen.toFixed(1)}%). Review pricing or renegotiate commissions.`
                  : `El canal "${peorCanal}" tiene bajo margen (${peorCanalMargen.toFixed(1)}%). Revisa precios o renegocia comisiones.`}
              </p>
            )}
            
          </div>
        </div>

        {/* Tendencia de Ventas */}
        <div className="bg-[#1e293b] rounded-xl p-4 border border-blue-900/30">
          <h4 className="text-cyan-400 text-sm font-bold mb-2 flex items-center gap-2">
            📈 {t('salesTrend') || 'Tendencia de Ventas'}
          </h4>
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-xs">{t('thisWeek') || 'Esta semana'}: ${(tendenciaVentas?.valorActual || 0).toLocaleString()}</span>
            <span className={`text-xs font-bold ${tendenciaVentas?.direccion === 'up' ? 'text-green-400' : 'text-red-400'}`}>
              {tendenciaVentas?.direccion === 'up' ? '↑' : '↓'} {tendenciaVentas?.porcentaje || 0}%
            </span>
          </div>
          <p className="text-gray-500 text-xs mt-2">
            {t('vsLastWeek') || 'vs semana pasada'}
          </p>

          {/* Resumen de canales */}
          {canales.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-2">
                {idioma === 'en' ? 'Channels' : 'Canales'}
              </p>
              {canales.slice(0, 3).map(canal => {
                const data = ventasPorCanal[canal];
                const porcentaje = ventasTotales > 0 ? ((data.ventas / ventasTotales) * 100) : 0;
                return (
                  <div key={canal} className="flex justify-between items-center text-xs py-1">
                    <span className="text-gray-400 truncate max-w-[80px]">{canal}</span>
                    <span className="text-gray-500">{(porcentaje).toFixed(0)}%</span>
                    <span className={`font-bold ${data.margen >= 50 ? 'text-green-400' : data.margen >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {data.margen.toFixed(0)}%
                    </span>
                  </div>
                );
              })}
              {canales.length > 3 && (
                <p className="text-gray-500 text-[10px] mt-1">
                  +{canales.length - 3} {idioma === 'en' ? 'more channels' : 'canales más'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardInsights;

